import React, { useState, useEffect, useRef } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";

interface RSSFeedWidgetProps {
  id: string;
  title: string;
  isDemo?: boolean;
  feed_url?: string;
  feeds?: string[];
  url?: string;
  limit?: number;
  sort?: 'asc' | 'desc';
  sort_by?: string;
}

interface RSSItem {
  title: string;
  link: string;
  description: string;
  pubDate: string;
  id: string; // Unique identifier for the item
  isNew?: boolean; // Flag to indicate if the item is new
  source?: string; // Source name of the feed
  sourceUrl?: string; // Source URL of the feed
}

const RSSFeedWidget: React.FC<RSSFeedWidgetProps> = ({ 
  id: widgetId,
  title, 
  isDemo = false, 
  feed_url,
  feeds = [],
  url,
  limit = 5,
  sort = 'desc',
  sort_by = 'pubDate'
}) => {
  const [items, setItems] = useState<RSSItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenItemIds, setHiddenItemIds] = useState<string[]>([]);
  
  // Ref to track if we should fetch the feeds (only once)
  const shouldFetchRef = useRef(true);
  
  // Store props in refs to use in the fetch without triggering re-renders
  const feedUrlRef = useRef(feed_url);
  const feedsRef = useRef(feeds);
  const limitRef = useRef(limit);
  const sortRef = useRef(sort);
  const sortByRef = useRef(sort_by);
  
  // Update refs when props change (without triggering effects)
  useEffect(() => {
    feedUrlRef.current = feed_url;
    feedsRef.current = feeds;
    limitRef.current = limit;
    sortRef.current = sort;
    sortByRef.current = sort_by;
  }, [feed_url, feeds, limit, sort, sort_by]);
  
  // Create a key for localStorage based on the feed URLs or widget ID
  const storageKey = `rss_widget_${widgetId}`;
  const lastSeenKey = `${storageKey}_last_seen`;
  const hiddenItemsKey = `${storageKey}_hidden_items`;

  // Load hidden items from localStorage
  useEffect(() => {
    try {
      const hiddenItems = localStorage.getItem(hiddenItemsKey);
      if (hiddenItems) {
        setHiddenItemIds(JSON.parse(hiddenItems));
      }
    } catch (err) {
      console.error("Error loading hidden items from localStorage:", err);
    }
  }, [hiddenItemsKey]);

  // Initialize feed data
  useEffect(() => {
    // Only fetch if this is the first time (shouldFetchRef is true)
    if (!shouldFetchRef.current) return;
    
    if (isDemo) {
      const now = new Date();
      const demoItems = [
        {
          id: "1",
          title: "Nieuw project gelanceerd",
          link: "#",
          description: "Het team heeft succesvol een nieuw project gelanceerd.",
          pubDate: new Date(now.getTime() - 15 * 60000).toISOString(), // 15 minutes ago
          isNew: true,
          source: "Demo Feed 1",
          sourceUrl: "https://example.com"
        },
        {
          id: "2",
          title: "Team update",
          link: "#",
          description: "Bekijk de laatste updates van het ontwikkelteam.",
          pubDate: new Date(now.getTime() - 2 * 3600000).toISOString(), // 2 hours ago
          isNew: true,
          source: "Demo Feed 2",
          sourceUrl: "https://demo.com"
        },
        {
          id: "3",
          title: "Komende events",
          link: "#",
          description: "Zie welke events er binnenkort gepland staan.",
          pubDate: new Date(now.getTime() - 24 * 3600000).toISOString(), // 1 day ago
          source: "Demo Feed 1",
          sourceUrl: "https://example.com"
        },
      ];
      setItems(demoItems);
      setLoading(false);
      shouldFetchRef.current = false; // Prevent future fetches
    } else if (feedsRef.current.length > 0 || feedUrlRef.current) {
      fetchRSSFeeds();
      shouldFetchRef.current = false; // Prevent future fetches
    } else {
      setError("Geen feed URLs opgegeven");
      setLoading(false);
      shouldFetchRef.current = false; // Prevent future fetches
    }
    
    // Cleanup function will be called when component unmounts
    return () => {
      shouldFetchRef.current = true; // Reset for next mount
    };
  // Empty dependency array means this only runs once after initial render
  }, [isDemo]);

  const fetchRSSFeeds = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the last time we checked these feeds
      let lastSeen = localStorage.getItem(lastSeenKey);
      const lastSeenDate = lastSeen ? new Date(lastSeen) : null;
      
      // Update the last seen time to now
      const now = new Date();
      localStorage.setItem(lastSeenKey, now.toISOString());
      
      // Determine which feeds to fetch
      let feedUrls: string[] = [];
      if (feedsRef.current.length > 0) {
        feedUrls = feedsRef.current;
      } else if (feedUrlRef.current) {
        feedUrls = [feedUrlRef.current];
      }
      
      if (feedUrls.length === 0) {
        throw new Error('Geen feed URLs opgegeven');
      }
      
      console.log(`Fetching ${feedUrls.length} RSS feeds...`);
      
      // Fetch all feeds in parallel
      const feedPromises = feedUrls.map(fetchSingleFeed);
      const feedResults = await Promise.allSettled(feedPromises);
      
      // Process results from all feeds
      let allItems: RSSItem[] = [];
      let successfulFeeds = 0;
      let totalItemCount = 0;
      
      feedResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          const feedItems = result.value;
          totalItemCount += feedItems.length;
          
          console.log(`Feed ${index + 1} (${feedUrls[index]}) loaded with ${feedItems.length} items`);
          
          // Add all items from this feed to the combined list
          allItems = [...allItems, ...feedItems];
          
          if (feedItems.length > 0) {
            successfulFeeds++;
          }
        } else if (result.status === 'rejected') {
          console.error(`Error fetching feed ${feedUrls[index]}:`, result.reason);
        }
      });
      
      console.log(`Successfully loaded ${successfulFeeds} feeds with ${totalItemCount} total items`);
      
      // Check if we have any items
      if (allItems.length === 0) {
        if (feedResults.every(result => result.status === 'rejected')) {
          throw new Error('Geen van de feeds kon worden geladen');
        }
        if (successfulFeeds === 0) {
          throw new Error('De feeds konden worden geladen, maar bevatten geen items');
        }
      }
      
      console.log(`Sorting ${allItems.length} combined RSS items`);
      
      // Sort all items by publication date
      allItems = sortItems(allItems, sortByRef.current, sortRef.current);
      
      // Filter out hidden items
      if (hiddenItemIds.length > 0) {
        console.log(`Filtering out ${hiddenItemIds.length} hidden items`);
        allItems = allItems.filter(item => !hiddenItemIds.includes(item.id));
      }
      
      // Limit the number of items
      const limitedItems = allItems.slice(0, limitRef.current);
      console.log(`Limiting to ${limitedItems.length} items from ${allItems.length} total`);
      
      setItems(limitedItems);
    } catch (err) {
      console.error('Error fetching RSS feeds:', err);
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden bij het laden van de feed');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to sort items
  const sortItems = (items: RSSItem[], sortBy: string, sortDirection: string): RSSItem[] => {
    return [...items].sort((a, b) => {
      let valA: any, valB: any;
      
      if (sortBy === 'pubDate' || sortBy === 'published_at') {
        valA = new Date(a.pubDate).getTime();
        valB = new Date(b.pubDate).getTime();
      } else if (sortBy === 'title') {
        valA = a.title.toLowerCase();
        valB = b.title.toLowerCase();
      } else {
        // For other keys, use type safety
        const key = sortBy as keyof RSSItem;
        valA = a[key] as any || '';
        valB = b[key] as any || '';
      }
      
      if (sortDirection === 'asc') {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
  };
  
  // Helper function to strip HTML tags from description
  const stripHtmlTags = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };
  
  // Shorten description to a max length
  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Handle feedback from user
  const handleRelevance = (id: string, isRelevant: boolean) => {
    if (!isRelevant) {
      // Mark item as not relevant (hide it)
      const newHiddenItemIds = [...hiddenItemIds, id];
      setHiddenItemIds(newHiddenItemIds);
      
      // Save to localStorage
      try {
        localStorage.setItem(hiddenItemsKey, JSON.stringify(newHiddenItemIds));
      } catch (err) {
        console.error("Error saving hidden items to localStorage:", err);
      }
      
      // Remove from current display
      setItems(items.filter(item => item.id !== id));
    }
  };

  // Format relative time for "posted X time ago"
  const getRelativeTime = (dateString: string): string => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    // Time brackets
    const minute = 60;
    const hour = minute * 60;
    const day = hour * 24;
    const week = day * 7;
    const month = day * 30;
    const year = day * 365;
    
    if (seconds < minute) {
      return "zojuist";
    } else if (seconds < hour) {
      const minutes = Math.floor(seconds / minute);
      return `${minutes} ${minutes === 1 ? 'minuut' : 'minuten'} geleden`;
    } else if (seconds < day) {
      const hours = Math.floor(seconds / hour);
      return `${hours} ${hours === 1 ? 'uur' : 'uur'} geleden`;
    } else if (seconds < week) {
      const days = Math.floor(seconds / day);
      return `${days} ${days === 1 ? 'dag' : 'dagen'} geleden`;
    } else if (seconds < month) {
      const weeks = Math.floor(seconds / week);
      return `${weeks} ${weeks === 1 ? 'week' : 'weken'} geleden`;
    } else if (seconds < year) {
      const months = Math.floor(seconds / month);
      return `${months} ${months === 1 ? 'maand' : 'maanden'} geleden`;
    } else {
      const years = Math.floor(seconds / year);
      return `${years} ${years === 1 ? 'jaar' : 'jaar'} geleden`;
    }
  };

  const fetchSingleFeed = async (feedUrl: string): Promise<RSSItem[]> => {
    // Extract domain for source attribution
    let sourceName = '';
    let sourceUrl = '';
    let sourceDomain = '';
    
    try {
      const url = new URL(feedUrl);
      sourceUrl = `${url.protocol}//${url.hostname}`;
      sourceDomain = url.hostname;
      sourceName = url.hostname.replace('www.', '');
    } catch (e) {
      sourceName = feedUrl;
      sourceUrl = feedUrl;
      sourceDomain = 'unknown';
    }
    
    try {
      let response;
      let data;
      
      // Try direct fetch first (will work if CORS is properly configured on the server)
      try {
        console.log(`Attempting direct fetch for ${feedUrl}`);
        response = await fetch(feedUrl, { 
          headers: { 'Accept': 'application/rss+xml, application/xml, text/xml' },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`Direct fetch failed with status ${response.status}`);
        }
        
        const xmlText = await response.text();
        console.log(`Direct fetch successful for ${feedUrl}`);
        return parseXmlToItems(xmlText, sourceName, sourceUrl, sourceDomain, feedUrl);
      } catch (directFetchErr) {
        // If direct fetch fails due to CORS or other issues, try proxies
        console.log(`Direct fetch failed for ${feedUrl}: ${directFetchErr instanceof Error ? directFetchErr.message : 'Unknown error'}, trying proxies...`);
        
        // First proxy attempt - allorigins
        try {
          const corsProxy1 = 'https://api.allorigins.win/get?url=';
          const encodedFeedUrl = encodeURIComponent(feedUrl);
          response = await fetch(`${corsProxy1}${encodedFeedUrl}`);
          
          if (!response.ok) {
            throw new Error(`First proxy failed with status ${response.status}`);
          }
          
          data = await response.json();
          console.log(`First proxy successful for ${feedUrl}`);
          return parseXmlToItems(data.contents, sourceName, sourceUrl, sourceDomain, feedUrl);
        } catch (proxy1Err) {
          console.log(`First proxy failed for ${feedUrl}: ${proxy1Err instanceof Error ? proxy1Err.message : 'Unknown error'}, trying second proxy...`);
          
          // Second proxy attempt - corsproxy.io
          const corsProxy2 = 'https://corsproxy.io/?';
          response = await fetch(`${corsProxy2}${feedUrl}`);
          
          if (!response.ok) {
            throw new Error(`Second proxy failed with status ${response.status}`);
          }
          
          // Parse XML directly from the response text
          const xmlText = await response.text();
          console.log(`Second proxy successful for ${feedUrl}`);
          return parseXmlToItems(xmlText, sourceName, sourceUrl, sourceDomain, feedUrl);
        }
      }
    } catch (err) {
      console.error(`All fetch methods failed for feed ${feedUrl}:`, err);
      // Return empty array instead of throwing to allow other feeds to still work
      return [];
    }
  };
  
  // Helper function to parse XML to RSS items
  const parseXmlToItems = (xmlText: string, sourceName: string, sourceUrl: string, sourceDomain: string, feedUrl: string): RSSItem[] => {
    // Get the last time we checked this feed
    let lastSeen = localStorage.getItem(lastSeenKey);
    const lastSeenDate = lastSeen ? new Date(lastSeen) : null;
    
    // Parse the XML content
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");
    
    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      console.error('XML parsing error:', parserError.textContent);
      return [];
    }
    
    // Try to get channel title for better source naming
    const channelTitle = xmlDoc.querySelector('channel > title')?.textContent;
    if (channelTitle) {
      sourceName = channelTitle.trim();
    }
    
    // Extract items from the RSS feed
    const itemElements = xmlDoc.querySelectorAll('item');
    let parsedItems: RSSItem[] = [];
    
    itemElements.forEach((item) => {
      try {
        const title = item.querySelector('title')?.textContent?.trim() || 'Geen titel';
        const link = item.querySelector('link')?.textContent?.trim() || '#';
        const description = item.querySelector('description')?.textContent?.trim() || '';
        
        // Try to get pubDate, or fallback to other date fields
        let pubDate = item.querySelector('pubDate')?.textContent?.trim() || 
                      item.querySelector('pubdate')?.textContent?.trim() || 
                      item.querySelector('dc\\:date')?.textContent?.trim() || 
                      new Date().toISOString();
        
        // Format the date consistently
        try {
          const dateObj = new Date(pubDate);
          if (!isNaN(dateObj.getTime())) {
            pubDate = dateObj.toISOString();
          }
        } catch (dateError) {
          console.warn('Could not parse date:', pubDate);
          // Keep original pubDate if parsing fails
        }
        
        // Generate a consistent ID for the item
        // Use guid if available, otherwise create one from link and title
        const guid = item.querySelector('guid')?.textContent?.trim();
        
        // Create a unique ID that includes the source domain to ensure no collisions between feeds
        const id = guid 
          ? `${sourceDomain}_${guid}`.replace(/[^a-z0-9]/gi, '_') 
          : `${sourceDomain}_${link}_${title}`.replace(/[^a-z0-9]/gi, '_');
        
        // Check if this item is newer than the last time we checked
        const pubDateTime = new Date(pubDate);
        const isNew = lastSeenDate ? pubDateTime > lastSeenDate : false;
        
        parsedItems.push({
          id,
          title,
          link,
          description: stripHtmlTags(description),
          pubDate,
          isNew,
          source: sourceName,
          sourceUrl
        });
      } catch (itemError) {
        console.error('Error parsing RSS item:', itemError);
        // Skip this item but continue with others
      }
    });
    
    console.log(`Parsed ${parsedItems.length} items from source: ${sourceName}`);
    return parsedItems;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-red-500">{error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Geen items gevonden</p>
          ) : (
            items.map((item, index) => (
              <div key={item.id} className="space-y-1 pb-3 border-b last:border-b-0 border-border">
                <div className="flex justify-between items-start">
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium hover:underline block"
                  >
                    {item.title}
                    {item.isNew && (
                      <span className="inline-flex ml-2 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                        Nieuw
                      </span>
                    )}
                  </a>
                </div>
                <p className="text-xs text-muted-foreground">
                  {truncateText(item.description, 120)}
                </p>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <p className="text-xs text-muted-foreground">
                      {getRelativeTime(item.pubDate)}
                    </p>
                    {item.source && (
                      <span className="text-xs bg-secondary/30 text-secondary-foreground/80 px-1.5 py-0.5 rounded-sm">
                        {item.source}
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleRelevance(item.id, false)}
                      title="Niet relevant"
                      className="text-xs text-muted-foreground hover:text-destructive p-1 rounded hover:bg-muted"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {(feedUrlRef.current || feedsRef.current.length > 0) && (
          <div className="mt-4 pt-2 border-t border-border">
            <a
              href={url || (feedsRef.current.length > 0 ? feedsRef.current[0] : feedUrlRef.current)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:underline"
            >
              Bekijk bron
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RSSFeedWidget; 